/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { Button, Card, IconAdd, IconSearch, Table } from "tvs-unified-lib";

import { InputField, SelectField } from "@/components";
import HierarchicalMultiSelect, {
  Option,
} from "@/components/HierarchicalMultiSelect";
import usePortfolioCategory from "@/services/hooks/portfolio/usePortfolio";
import {
  useTemplateCopy,
  useTemplateList,
} from "@/services/hooks/template/useTemplate";
import { PortfolioItem, PortfolioItemChild } from "@/services/network/type";
import debounce from "lodash/debounce";
import { useSnackbar } from "notistack";
import { useForm } from "react-hook-form";
import { channelOptions, templateColumns } from "./helpers";
import { GroupButton, Header, Root, Title } from "./styles";
import { DataType, Pagination } from "./types";

interface FormValues {
  search: string;
  channel: { value: string; label: string }[];
  portfolio_category_id: any[];
}

export const TemplatePages = () => {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const isInitialMount = useRef(true);

  const [params, setParams] = useState<{
    page: number;
    limit: number;
    search: string;
    filter?: string;
  }>({
    page: 1,
    limit: 10,
    search: "",
  });

  const { data: portfolioCategory } = usePortfolioCategory();
  const {
    data: templateList,
    isLoading,
    refetch: fetchData,
  } = useTemplateList(params);
  const { mutation: copyTemplate, isPending: isCopy } = useTemplateCopy();

  const [data, setData] = useState<DataType[]>([]);
  const [portfolioOptions, setPortfolioOptions] = useState<Option[]>([]);

  const [pagination, setPagination] = useState<Pagination>({
    pageIndex: 0,
    pageSize: 10,
    total: 0,
  });
  const { control, watch } = useForm<FormValues>({
    defaultValues: {
      search: "",
    },
  });
  const searchValue = watch("search");
  const channelValue = watch("channel");
  const portfolioCategoryValue = watch("portfolio_category_id");

  const debouncedSetParams = useCallback(
    debounce(() => {
      const currentChannel = channelValue ? channelValue[0].value : "";
      const currentPortfolioIds = portfolioCategoryValue?.map(
        (item) => item.value
      );
      const filterObj = {
        ...(currentChannel && { channel: currentChannel }),
        ...(currentPortfolioIds?.length && {
          "portfolio.id": currentPortfolioIds,
        }),
      };

      setParams({
        page: pagination.pageIndex + 1,
        limit: pagination.pageSize || 10,
        search: searchValue,
        filter:
          Object.keys(filterObj).length > 0
            ? JSON.stringify(filterObj)
            : undefined,
      });
    }, 500),
    [
      searchValue,
      channelValue,
      portfolioCategoryValue,
      pagination.pageIndex,
      pagination.pageSize,
      setParams,
    ]
  );

  useEffect(() => {
    if (isInitialMount.current) {
      return;
    }
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, [searchValue, channelValue, portfolioCategoryValue, setPagination]);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    debouncedSetParams();
    return () => {
      debouncedSetParams.cancel();
    };
  }, [debouncedSetParams]);

  useEffect(() => {
    fetchData();
  }, [params, fetchData]);

  useEffect(() => {
    if (templateList) {
      setData(templateList.data.response);
      setPagination((prev) => ({
        ...prev,
        pageIndex: templateList.data.currentPage - 1,
        pageSize: templateList.data.perPage,
        total: templateList.data.totalCount,
      }));
    }
  }, [templateList, setData, setPagination]);

  useEffect(() => {
    if (portfolioCategory) {
      setPortfolioOptions(
        portfolioCategory.map((item: PortfolioItem) => ({
          label: item.name,
          value: item.name,
          name: item.name,
          children: item.items.map((child: PortfolioItemChild) => ({
            label: child.value,
            value: child.id,
            name: child.name,
          })),
        }))
      );
    }
  }, [portfolioCategory]);

  const handlePageChange = (pageIndex: number) => {
    setPagination((prev) => ({ ...prev, pageIndex }));
  };

  const copyTemplates = async (id) => {
    if (isCopy) return;
    await copyTemplate.mutateAsync(id, {
      onSuccess: () => {
        enqueueSnackbar("Template copied successfully.", {
          variant: "success",
        });
        fetchData();
      },
      onError: (error) => {
        enqueueSnackbar(error.message, {
          variant: "error",
        });
      },
    });
  };

  const columns = templateColumns(copyTemplates);

  return (
    <Root>
      <Header>
        <Title>Templates</Title>
        <Button
          onClick={() => navigate(`/templates/create`)}
          label="New Template"
          icon={<IconAdd fill="white" />}
          iconPosition="left"
          theme="purple"
        />
      </Header>
      <Card variant="no-border" className="card-filter">
        <GroupButton>
          <SelectField
            name="channel"
            placeholder="All Channels"
            control={control}
            options={channelOptions}
          />
          <HierarchicalMultiSelect
            options={portfolioOptions}
            control={control}
            name="portfolio_category_id"
            rootTitle="All Portfolios"
            placeholder="Select Portfolios..."
          />
          <InputField
            name="search"
            control={control}
            placeholder="Search By Template Name, Created By, Updated By"
            icon={<IconSearch />}
          />
        </GroupButton>
      </Card>

      <Table<DataType>
        columns={columns}
        data={data}
        totalRecords={pagination.total || 0}
        isLoading={isLoading}
        manualSorting={false}
        pagination={{
          pageIndex: pagination.pageIndex,
          pageSize: pagination.pageSize || 10,
        }}
        enablePagination={true}
        onPageChange={handlePageChange}
        sorting={[]}
        setSorting={() => {}}
      />
    </Root>
  );
};

export default TemplatePages;
